/**
 * AccessOracle — storage único de estado de acesso processual.
 *
 * Consolida três responsabilidades antes espalhadas:
 *   1. Memo IDB de decisões de acesso (offline-first).
 *   2. Gate UI (`AccessState.kind`) consumido pelo `useProcessoJudicial`.
 *   3. `queryClient.removeQueries` ao registrar negação (suprime data antiga).
 *
 * Os 3 caminhos de negação chamam `oracle.recordDenied(...)` e o oracle faz
 * o write atômico (memória + IDB + removeQueries + notifica subscribers via
 * `useSyncExternalStore`):
 *   - useEffect de erro 403 da query (dentro de `useProcessoJudicial`).
 *   - Handler SSE `onAcessoRevogado` (em `AutosPage`).
 *   - useEffect observando `sse.lastError` em onopen 403.
 *
 * Bootstrap (em `main.tsx` ou wrapper de auth, após Auth0 entregar user):
 *   const oracle = createAccessOracle({ queryClient, userId: user.sub });
 *   <AccessOracleProvider value={oracle}><App /></AccessOracleProvider>
 *
 * Recriado no login (novo userId); destruído no logout via `oracle.clear()`.
 */

import type { QueryClient } from '@tanstack/react-query';
import { createContext, useContext, useSyncExternalStore } from 'react';
import { del, get as idbGet, keys, set as idbSet } from 'idb-keyval';

import { queryKeys } from '../pwa/queryConfig';

/** A chave de acesso aos autos de um colega (`useChaveDeAcesso`): `dono:<id>`. */
export const PREFIXO_CHAVE_DO_DONO = 'dono:';

export type AccessSource = 'live' | 'memo' | 'event';

export type AccessState =
  | { kind: 'unknown' }
  | {
      kind: 'allowed';
      tipoAcesso: string;
      validadoEm: number; // epoch ms
      source: AccessSource;
    }
  | {
      kind: 'denied';
      codigo: string;
      tipoAcesso: string;
      motivo: string | null;
      validadoEm: number;
      source: AccessSource;
    };

/**
 * Singleton para o estado `unknown` — `getSnapshot` precisa devolver sempre a
 * mesma referência quando não há decisão registrada, senão o `useSyncExternalStore`
 * detecta "mudança" a cada render (Object.is) e entra em loop infinito.
 */
const UNKNOWN_STATE: AccessState = { kind: 'unknown' };

export interface AccessOracle {
  /** Snapshot síncrono — sem tearing entre componentes. */
  getSnapshot(numero: string, cpf: string): AccessState;
  /** Notifica em mutations. Retorna unsubscribe. */
  subscribe(numero: string, cpf: string, listener: () => void): () => void;
  recordAllowed(
    numero: string,
    cpf: string,
    tipoAcesso: string,
    source: AccessSource,
  ): Promise<void>;
  recordDenied(
    numero: string,
    cpf: string,
    codigo: string,
    tipoAcesso: string,
    motivo: string | null,
    source: AccessSource,
  ): Promise<void>;
  /** Carrega decisão persistida em IDB para a memória. Idempotente. */
  hydrateFromMemo(numero: string, cpf: string): Promise<void>;
  /** Logout / clearOfflineCaches. Limpa memória + IDB do user atual. */
  clear(): Promise<void>;
}

interface CreateAccessOracleOptions {
  queryClient: QueryClient;
  userId: string;
}

const STORE_PREFIX = 'jurisapp:access';

function memoKey(userId: string, numero: string, cpf: string): string {
  return `${STORE_PREFIX}:${userId}:${numero}:${cpf}`;
}

function userPrefix(userId: string): string {
  return `${STORE_PREFIX}:${userId}:`;
}

function stateKey(numero: string, cpf: string): string {
  return `${numero}:${cpf}`;
}

interface PersistedAccess {
  kind: 'allowed' | 'denied';
  tipoAcesso: string;
  validadoEm: number;
  codigo?: string;
  motivo?: string | null;
}

export function createAccessOracle(opts: CreateAccessOracleOptions): AccessOracle {
  const { queryClient, userId } = opts;
  const states = new Map<string, AccessState>();
  const listeners = new Map<string, Set<() => void>>();

  function notify(numero: string, cpf: string): void {
    const set = listeners.get(stateKey(numero, cpf));
    if (!set) return;
    for (const l of set) l();
  }

  return {
    getSnapshot(numero: string, cpf: string): AccessState {
      return states.get(stateKey(numero, cpf)) ?? UNKNOWN_STATE;
    },

    subscribe(numero: string, cpf: string, listener: () => void): () => void {
      const k = stateKey(numero, cpf);
      let set = listeners.get(k);
      if (!set) {
        set = new Set();
        listeners.set(k, set);
      }
      set.add(listener);
      return () => {
        set?.delete(listener);
        if (set && set.size === 0) listeners.delete(k);
      };
    },

    async recordAllowed(numero, cpf, tipoAcesso, source) {
      const next: AccessState = {
        kind: 'allowed',
        tipoAcesso,
        validadoEm: Date.now(),
        source,
      };
      states.set(stateKey(numero, cpf), next);
      const persisted: PersistedAccess = {
        kind: 'allowed',
        tipoAcesso,
        validadoEm: next.validadoEm,
      };
      await safeIdbSet(memoKey(userId, numero, cpf), persisted);
      notify(numero, cpf);
    },

    async recordDenied(numero, cpf, codigo, tipoAcesso, motivo, source) {
      const next: AccessState = {
        kind: 'denied',
        codigo,
        tipoAcesso,
        motivo,
        validadoEm: Date.now(),
        source,
      };
      states.set(stateKey(numero, cpf), next);
      const persisted: PersistedAccess = {
        kind: 'denied',
        tipoAcesso,
        validadoEm: next.validadoEm,
        codigo,
        motivo,
      };
      // Na chave do dono (processo de um colega) a negativa fica só na memória: o backend
      // pode ter lido com o certificado de quem lê, e no disco ela barraria o processo
      // mesmo depois de o dono configurar o dele.
      if (!cpf.startsWith(PREFIXO_CHAVE_DO_DONO)) await safeIdbSet(memoKey(userId, numero, cpf), persisted);
      // Remove dados visíveis da query — sem isso, React Query mantém `data`
      // antigo durante o erro e o usuário veria autos sob o modal de revogação.
      queryClient.removeQueries({ queryKey: queryKeys.processual.consulta(numero) });
      notify(numero, cpf);
    },

    async hydrateFromMemo(numero, cpf) {
      const k = stateKey(numero, cpf);
      // Já hidratado nesta sessão? Não sobrescreve — chamadas de UI ao
      // hydrateFromMemo são idempotentes do ponto de vista de tela.
      if (states.has(k)) {
        notify(numero, cpf);
        return;
      }
      const persisted = await safeIdbGet<PersistedAccess>(memoKey(userId, numero, cpf));
      if (!persisted) {
        // Notifica subscribers (kind permanece 'unknown'); sem isso, query
        // fica bloqueada esperando hidratação que já terminou.
        notify(numero, cpf);
        return;
      }
      const restored: AccessState =
        persisted.kind === 'allowed'
          ? {
              kind: 'allowed',
              tipoAcesso: persisted.tipoAcesso,
              validadoEm: persisted.validadoEm,
              source: 'memo',
            }
          : {
              kind: 'denied',
              codigo: persisted.codigo ?? 'ACESSO_NEGADO_PRIVADO',
              tipoAcesso: persisted.tipoAcesso,
              motivo: persisted.motivo ?? null,
              validadoEm: persisted.validadoEm,
              source: 'memo',
            };
      states.set(k, restored);
      notify(numero, cpf);
    },

    async clear() {
      states.clear();
      listeners.clear();
      // Apaga apenas as chaves do user atual no IDB
      try {
        const allKeys = await keys();
        const prefix = userPrefix(userId);
        await Promise.all(
          allKeys
            .filter((k): k is string => typeof k === 'string' && k.startsWith(prefix))
            .map((k) => del(k)),
        );
      } catch {
        // IDB indisponível (modo privado, quota excedida, etc.) — limpeza de
        // memória já foi feita; persistência fica no disco até overflow LRU.
      }
    },
  };
}

async function safeIdbGet<T>(k: string): Promise<T | undefined> {
  try {
    return (await idbGet<T>(k)) ?? undefined;
  } catch {
    return undefined;
  }
}

async function safeIdbSet<T>(k: string, value: T): Promise<void> {
  try {
    await idbSet(k, value);
  } catch {
    // Falha de IDB não derruba a UI — memória + queryClient já refletem o estado.
  }
}

// ---------------------------------------------------------------------------
// React glue
// ---------------------------------------------------------------------------

const AccessOracleContext = createContext<AccessOracle | null>(null);

export const AccessOracleProvider = AccessOracleContext.Provider;

export function useAccessOracle(): AccessOracle {
  const ctx = useContext(AccessOracleContext);
  if (!ctx) {
    throw new Error('useAccessOracle requer AccessOracleProvider — instalar em main.tsx após login');
  }
  return ctx;
}

/**
 * Hook idiomático React 18+ — re-renderiza quando o oracle notifica para
 * essa tupla (numero, cpf). Sem tearing graças ao `useSyncExternalStore`.
 */
export function useAccessState(numero: string, cpf: string): AccessState {
  const oracle = useAccessOracle();
  return useSyncExternalStore(
    (cb) => oracle.subscribe(numero, cpf, cb),
    () => oracle.getSnapshot(numero, cpf),
  );
}
